// Unpublished Work © 2026 Deere & Company.

import React, {useState, useEffect, useMemo} from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {isAbortError, useDeepMemo} from 'Utils/react-utils';
import moment from 'moment';
import {Box, DateRangeChip, MultiselectChip, SegmentedControl, Stack, Typography} from '@deere/isg.component-library';
import DataGridWrapper from 'Ui/components/common/data-table/data-grid-wrapper';
import NoDataMessage from 'Ui/components/common/message/no-data-message';
import {getDateRangePresets, getGroupedEquipmentByModel} from './utils/equipment-report-utils';
import {getFormattedCurrency} from 'Utils/unit-conversion-utils';
import {getPastServiceFromEquipment} from 'Services/maintenance-service';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {datadogLogs} from '@datadog/browser-logs';

const MINUTES_PER_HOUR = 60;

function formatServiceTime(value) {
    if (value === null || value === undefined) {
        return '';
    }

    if (!Number.isFinite(value) || value < 0) {
        datadogLogs.logger.error('Invalid totalServiceTime in equipment costs report');

        return '';
    }

    const totalMinutes = Math.round(value * MINUTES_PER_HOUR);

    return `${Math.floor(totalMinutes / MINUTES_PER_HOUR)}h ${totalMinutes % MINUTES_PER_HOUR}m`;
}

function getDataGridColumns(translations, currencyCell) {
    return [
        {
            headerName: translations.ONLINK_EQUIPMENT_NICKNAME,
            field: 'equipmentName',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => params.value
        },
        {
            headerName: translations.BRAND,
            field: 'manufacturerName',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => params.value
        },
        {
            headerName: translations.MODEL,
            field: 'modelName',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => params.value
        },
        {
            headerName: translations.ONLINK_PARTS_COST,
            field: 'totalPartsCost',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                return currencyCell({
                    value: params.value
                });
            }
        },
        {
            headerName: translations.ONLINK_LABOR_COST,
            field: 'totalLaborCost',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                return currencyCell({
                    value: params.value
                });
            }
        },
        {
            headerName: translations.ONLINK_TOTAL,
            field: 'totalCost',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                return currencyCell({
                    value: params.value
                });
            }
        },
        {
            headerName: translations.ONLINK_SERVICE_TIME || 'Service Time',
            field: 'totalServiceTime',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => formatServiceTime(params.value)
        }
    ];
}
function EquipmentCostsV2(props) {
    const {
        equipment,
        membership,
        versionButtonsConf,
        versionButtonsIndex,
        translations
    } = props;

    const MULTI_SELECT_LABELS = {
        emptyList: translations.ONLINK_WORKBOARD_NO_RESULTS,
        noItemMatch: translations.NO_RESULTS_FOUND,
        allSelect: translations.ALL_SELECTED
    };

    const currencyCell = React.useCallback(({value}) => getFormattedCurrency(value, {
        currencyPreference: membership.currencyPreference
    }), [membership.currencyPreference]);

    const {addToast} = useToast();

    const [dateRangeSelection, setDateRangeSelection] = useState(
        () => [moment().startOf('month').toDate(), moment().endOf('month').toDate()]
    );
    const [selectedIds, setSelectedIds] = useState([]);
    const [serviceData, setServiceData] = useState({});
    const [loadingTable, setLoadingTable] = useState(false);

    const tableData = useDeepMemo(() => {
        const match = equipment.find((eq) => eq.equipmentId === selectedIds[0]);

        return [{
            ...match,
            ...serviceData
        }];
    }, [equipment, selectedIds, serviceData]);

    const updateDateRange = React.useCallback((newDates) => {
        const [startValue, endValue] = newDates;
        const hasRange = Boolean(startValue) && Boolean(endValue);
        const resolvedDates = hasRange ?
            newDates :
            [moment().startOf('month').toDate(), moment().endOf('month').toDate()];

        setLoadingTable(true);
        setServiceData({});
        setDateRangeSelection(resolvedDates);
    }, [setDateRangeSelection]);

    const handleEquipmentChange = (selectedIds) => {
        setLoadingTable(true);
        setServiceData({});
        setSelectedIds([...selectedIds]);
    };

    useEffect(() => {
        const controller = new AbortController();

        if (selectedIds.length === 0) {
            setServiceData({});
            setLoadingTable(false);

            return () => controller.abort();
        }

        const [startDate, endDate] = dateRangeSelection;
        const fetchPastServices = async () => {
            try {
                const serviceTotals = await getPastServiceFromEquipment(
                    selectedIds[0],
                    moment(startDate).startOf('day').format(),
                    moment(endDate).endOf('day').format(),
                    controller.signal
                );

                // The response can arrive just as the request is abandoned; handing it to the catch keeps one way out.
                controller.signal.throwIfAborted();
                setServiceData(serviceTotals);
            } catch (error) {
                if (!controller.signal.aborted && !isAbortError(error)) {
                    addToast({
                        message: translations.ONLINK_RETRIEVING_INFORMATION_ERROR_MESSAGE,
                        type: TOAST_TYPE.ERROR
                    });
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoadingTable(false);
                }
            }
        };

        fetchPastServices();

        return () => controller.abort();
    }, [selectedIds, dateRangeSelection]);

    const dateRangePresets = useDeepMemo(() => getDateRangePresets(translations, moment), [translations]);

    const multiSelectModelOptions = useMemo(() => {
        const {groupedEquipment} = getGroupedEquipmentByModel(equipment);

        return {
            groups: groupedEquipment,
            items: [],
            groupOptions: {
                collapseAll: true
            }
        };
    }, [equipment]);

    return (
        <Stack
            sx={{
                padding: '1rem'
            }}
        >
            <Stack
                alignItems='center'
                direction='row'
                justifyContent='space-between'
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: '1rem'
                    }}
                >
                    <DateRangeChip
                        dateRangePresets={dateRangePresets}
                        isPersistent={true}
                        onChange={updateDateRange}
                        value={dateRangeSelection}
                    />
                    <MultiselectChip
                        {...multiSelectModelOptions}
                        className='equipment-models'
                        disableCloseOnClick
                        labels={MULTI_SELECT_LABELS}
                        loading={false}
                        onChange={(selectedIds) => handleEquipmentChange(selectedIds)}
                        selectedIds={selectedIds}
                        single
                        title={translations.ONLINK_EQUIPMENT_MODEL}
                    />
                </Box>
                <SegmentedControl
                    buttons={versionButtonsConf}
                    className='version-buttons'
                    selectedIndex={versionButtonsIndex}
                />
            </Stack>
            <Box
                sx={selectedIds.length === 0 ? {
                    marginTop: '2.5rem',
                    flexDirection: 'column',
                    display: 'flex',
                    alignItems: 'center'
                } : {
                    marginTop: '1rem'
                }}
            >
                <NoDataMessage
                    hasData={selectedIds.length > 0}
                    noDataMessage={translations.ONLINK_NO_DATA_GRAPH}
                >
                    <DataGridWrapper
                        columns={getDataGridColumns(translations, currencyCell)}
                        getRowId={(row) => row.equipmentId}
                        loading={loadingTable}
                        rows={tableData}
                        slotProps={{
                            root: {
                                disableColumnMenu: true
                            }
                        }}
                        translations={translations}
                    />
                </NoDataMessage>
                {selectedIds.length === 0 && (
                    <Typography
                        sx={{
                            marginTop: '1rem'
                        }}
                        textAlign='center'
                        variant='labelLargeSemibold'
                    >
                        {translations.ONLINK_SELECT_EQUIPMENT_MODEL}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}

EquipmentCostsV2.propTypes = {
    equipment: PropTypes.arrayOf(PropTypes.object),
    membership: PropTypes.membership,
    translations: PropTypes.translations,
    versionButtonsConf: PropTypes.arrayOf(PropTypes.object),
    versionButtonsIndex: PropTypes.number
};

export function mapStateToProps(state) {
    return {
        membership: state.membership,
        translations: state.translations
    };
}

export default connect(mapStateToProps)(EquipmentCostsV2);
